'use client';

import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// The only interactive part of the home page carousels. The carousels were
// client components for the sake of these two buttons, so every card in them
// was shipped as JavaScript and hydrated; now the cards are server-rendered
// and this is the whole client side.
//
// Inside the carousel, over its faded edges. They used to sit 20px outside
// it, and the home sections render lazily (content-visibility: auto), which
// clips anything past a section's edge - half of each arrow was cut off.
// Each arrow shows only when there is somewhere to go: none at all when every
// card fits, no "previous" at the start, no "next" at the end.

const BUTTON =
  'hidden md:flex absolute top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-neutral-900 border border-neutral-800 shadow-md items-center justify-center text-neutral-300 hover:text-white active:scale-95 transition opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20 cursor-pointer';

// A pixel or two of rounding at either end is not "more to scroll".
const EDGE = 2;

export default function CarouselArrows({
  targetId,
  step,
  labels = { prev: 'Previous', next: 'Next' },
  hoverScale = 'hover:scale-110',
}: {
  /** id of the horizontally scrolling element. */
  targetId: string;
  /** How far one press scrolls, in pixels. */
  step: number;
  labels?: { prev: string; next: string };
  hoverScale?: 'hover:scale-105' | 'hover:scale-110';
}) {
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  useEffect(() => {
    const track = document.getElementById(targetId);
    if (!track) return;
    const update = () => {
      setCanPrev(track.scrollLeft > EDGE);
      setCanNext(track.scrollLeft + track.clientWidth < track.scrollWidth - EDGE);
    };
    const frame = requestAnimationFrame(update);
    track.addEventListener('scroll', update, { passive: true });
    const resize = new ResizeObserver(update);
    resize.observe(track);
    return () => {
      cancelAnimationFrame(frame);
      track.removeEventListener('scroll', update);
      resize.disconnect();
    };
  }, [targetId]);

  const scroll = (direction: -1 | 1) => {
    document.getElementById(targetId)?.scrollBy({ left: direction * step, behavior: 'smooth' });
  };
  return (
    <>
      {canPrev && (
        <button type="button" onClick={() => scroll(-1)} className={`${BUTTON} ${hoverScale} left-2`} aria-label={labels.prev} aria-controls={targetId}>
          <ChevronLeft className="w-5 h-5" />
        </button>
      )}
      {canNext && (
        <button type="button" onClick={() => scroll(1)} className={`${BUTTON} ${hoverScale} right-2`} aria-label={labels.next} aria-controls={targetId}>
          <ChevronRight className="w-5 h-5" />
        </button>
      )}
    </>
  );
}
